import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import useAuthenticatedRestCall from '../hooks/useAuthenticatedRestCall';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { shortDateFormatter } from '../util/gridUtils';

import Chip from '@material-ui/core/Chip';
import Page from '../components/Page';
import { DataGrid } from '@material-ui/data-grid';
import ClientContext from '../contexts/ClientContext';

const useStyles = makeStyles({
  grid: {
    marginTop: '3rem',
    color: 'var(--darker-grey)',
    '& .MuiDataGrid-row': {
      '&:hover': {
        '&::before': {
          opacity: 1,
          transform: 'translateY(0)',
        },
      },
    },
    '& .MuiDataGrid-cell[data-field="reportDate"]': {
      fontSize: '1.5rem',
    },
    '& .MuiDataGrid-colCellTitle': {
      fontWeight: 'normal',
      color: '#3A3B3F',
    },
  },
  readyChip: {
    color: 'white',
  },
  downloadBtn: {
    textTransform: 'none',
    color: 'var(--garnish-dark-green)',
    fontWeight: 'bold',
    fontSize: '1.4rem'
  }
});

// --- NEW COMPONENT: Download Menu ---
const ReportDownloadMenu = ({ report }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { clientId, reportDate, id } = report;
  const dateStr = new Date(reportDate).toISOString().split('T')[0];
  const baseUrl = `${process.env.REACT_APP_REPORT_STORAGE_URL}/${clientId}`;

  const downloadFile = (filename) => {
    const url = `${baseUrl}/${filename}-${dateStr}-${id}.csv`;
    window.location.href = url;
    handleClose();
  };

  return (
    <div>
      <Button
        className={classes.downloadBtn}
        endIcon={<ArrowDropDownIcon />}
        onClick={handleClick}
      >
        Download CSVs
      </Button>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem onClick={() => downloadFile('user-report-general')}>
          User Report (General)
        </MenuItem>
        <MenuItem onClick={() => downloadFile('performance-by-level')}>
          Performance by Level
        </MenuItem>
        <MenuItem onClick={() => downloadFile('performance-by-course')}>
          Performance by Course
        </MenuItem>
        <MenuItem onClick={() => downloadFile('question-metadata')}>
          Question Metadata
        </MenuItem>
      </Menu>
    </div>
  );
};

const renderStatus = (params) => {
  const { status, completed } = params.row;

  if (status === 'failed') {
    return (
      <Chip 
        style={{ backgroundColor: '#e57373', color: 'white' }} 
        label="Failed" 
        size="small" 
      />
    );
  }

  // Check for success (Legacy OR New Reports). Legacy reports will have no "status" field, and will just show as "complete == true" for successful reports
  if (completed === true || status === 'success') {
    return (
      <Chip
        style={{ backgroundColor: '#60C2A7', color: 'white' }} 
        label="Ready"
        size="small"
      />
    );
  }
  return (
    <Chip 
      label="Processing" 
      color="default" 
      size="small" 
      variant="outlined" 
    />
  );
};

const renderDownloadLink = (params) => {
  const { clientId, status, completed, reportDate, id } = params.row;
  
  if (status === 'failed') {
    return <span style={{ color: '#e57373', fontSize: '0.9em' }}>Error generating report</span>;
  }

  // 1. New Reports: Show Dropdown Menu
  if (status === 'success') {
    return <ReportDownloadMenu report={params.row} />;
  }

  // 2. Legacy Reports: Show Single Link Fallback
  if (completed === true) {
    const date = new Date(reportDate).toISOString().split('T')[0];
    const reportUrl = `${process.env.REACT_APP_REPORT_STORAGE_URL}/${clientId}/user-report-${date}-${id}.csv`;
    return <a href={reportUrl} style={{ color: 'var(--garnish-dark-green)', fontWeight: 'bold' }}>Download CSV</a>;
  }

  return null;
};

const renderWithShortDate = (params) => shortDateFormatter(params.value);

const columns = [
  {
    field: 'reportDate',
    headerName: 'Report Date',
    flex: 1,
    renderCell: renderWithShortDate,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.25,
    align: 'center',
    headerAlign: 'center',
    renderCell: renderStatus,
  },
  {
    field: 'progress',
    headerName: 'Download Report',
    align: 'center',
    headerAlign: 'center',
    flex: 0.4,
    sortable: false,
    renderCell: renderDownloadLink,
  },
];

function Reports() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [pageSize, setPageSize] = useState(10);
  const [totalDocsCount, setTotalDocsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { clientInfo } = useContext(ClientContext);
  const makeAuthenticatedRequest = useAuthenticatedRestCall();

  const fetchReports = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest(
        `${process.env.REACT_APP_REPORT_SERVICE_URL}/reports/${
          clientInfo.clientId
        }?sortField=reportDate&sortDirection=desc&pageSize=${pageSize}&pageNumber=${
          pageNumber + 1
        }`
      );

      setTotalDocsCount(response.data.totalDocs);
      setRows(response.data.docs);
    } catch (error) {
      console.error(error);
      setError(error);
    }
  }, [pageNumber, pageSize, clientInfo.clientId, makeAuthenticatedRequest]);

  useEffect(() => {
    if (clientInfo.clientsHaveLoaded) {
      setLoading(true);
      fetchReports();
      setLoading(false);
      const interval = setInterval(() => {
        fetchReports();
      }, 5000); // Polling every 5 seconds for better responsiveness
      return () => clearInterval(interval);
    }
  }, [fetchReports, clientInfo.clientsHaveLoaded]);

  const handlePageSizeChange = useCallback((pageUpdate) => {
    const pageSize =
      typeof pageUpdate.pageSize === undefined
        ? pageUpdate
        : pageUpdate.pageSize;
    setPageSize(pageSize);
  }, []);

  const handlePageNumberChange = useCallback((pageUpdate) => {
    const pageNumber =
      typeof pageUpdate.page === undefined ? pageUpdate : pageUpdate.page;

    setPageNumber(pageNumber);
  }, []);

  const handleRunReportClicked = () => {
    makeAuthenticatedRequest(
      `${process.env.REACT_APP_REPORT_SERVICE_URL}/reports/${clientInfo.clientId}/new-report`,
      false,
      {
        data: {},
        method: 'POST',
      }
    )
      .then(() => {
        enqueueSnackbar(`Report Initiated!`, {
          variant: 'success',
        });
        fetchReports();
      })
      .catch(() => {
        enqueueSnackbar('An error occurred starting the report', {
          variant: 'error',
        });
      });
  };

  const toolbarButtons = () => (
    <React.Fragment>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleRunReportClicked}
      >
        Run New Report
      </Button>
    </React.Fragment>
  );

  return (
    <Page title="Reports" toolbarContent={toolbarButtons()}>
      <DataGrid
        className={classes.grid}
        rows={rows}
        columns={columns}
        autoHeight
        pageSize={pageSize}
        rowsPerPageOptions={[10, 20, 50, 100]}
        pagination
        paginationMode="server"
        onPageChange={handlePageNumberChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        rowCount={totalDocsCount}
        error={error}
        disableColumnSelector
        disableSelectionOnClick
      />
    </Page>
  );
}

export default Reports;